import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { fmtDateLong } from "@/lib/format";
import { SWAP_STATUS } from "@/lib/status";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Repeat, ArrowRight, Loader2, Check, X } from "lucide-react";

export default function SwapsManage() {
  const [swaps, setSwaps] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/swaps");
      setSwaps(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const action = async (id, act) => {
    try {
      await api.post(`/swaps/${id}/${act}`);
      toast.success("Bijgewerkt");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const pending = swaps.filter((s) => s.status === "accepted");
  const rest = swaps.filter((s) => s.status !== "accepted");

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;

  const Item = ({ s, actions }) => {
    const st = SWAP_STATUS[s.status] || { label: s.status, cls: "bg-slate-100 text-slate-600" };
    return (
      <Card className="p-4" data-testid={`manage-swap-${s.id}`}>
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2 text-sm">
            <span className="font-medium">{s.from_name}</span><ArrowRight className="w-4 h-4 text-muted-foreground" /><span className="font-medium">{s.to_name}</span>
          </div>
          <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${st.cls}`}>{st.label}</span>
        </div>
        {s.service && <p className="text-sm text-muted-foreground mt-2">{s.service.name} · {fmtDateLong(s.service.date)} · {s.service.start_time}–{s.service.end_time}</p>}
        {actions && (
          <div className="flex gap-2 mt-3">
            <Button size="sm" onClick={() => action(s.id, "approve")} data-testid={`approve-${s.id}`}><Check className="w-4 h-4 mr-1" /> Goedkeuren</Button>
            <Button size="sm" variant="outline" onClick={() => action(s.id, "decline")} data-testid={`decline-${s.id}`}><X className="w-4 h-4 mr-1" /> Afwijzen</Button>
          </div>
        )}
      </Card>
    );
  };

  return (
    <div className="space-y-6" data-testid="swaps-manage-page">
      <div>
        <h1 className="font-heading text-3xl font-extrabold tracking-tight">Ruilverzoeken beheren</h1>
        <p className="text-muted-foreground mt-1">Keur geaccepteerde ruilverzoeken goed of af.</p>
      </div>
      {swaps.length === 0 ? (
        <Card className="p-10 text-center text-muted-foreground"><Repeat className="w-10 h-10 mx-auto mb-3 opacity-40" /> Geen ruilverzoeken.</Card>
      ) : (
        <>
          {pending.length > 0 && (
            <div className="space-y-2">
              <h3 className="font-heading font-semibold">Wacht op goedkeuring ({pending.length})</h3>
              {pending.map((s) => <Item key={s.id} s={s} actions />)}
            </div>
          )}
          <div className="space-y-2">
            <h3 className="font-heading font-semibold">Alle verzoeken</h3>
            {rest.map((s) => <Item key={s.id} s={s} />)}
          </div>
        </>
      )}
    </div>
  );
}
